import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../../lib/i18next";
import {
  GLOBAL_REPORT_GOALS_EXCEEDED,
  VALUE_NUMBER,
  VALUE_WITH_PERCENTAGE,
} from "../../../i18n/keysTranslations";
import {
  GLOBAL_REPORT_GOAL_ACHIEVED_COLOR,
  GLOBAL_REPORT_GOAL_REMAINING_COLOR,
} from "../utils/globalReportChartPalette";

const PROGRESS_BAR_HEIGHT = 32;
const PROGRESS_BAR_RADIUS = 8;

const LegendItem = ({ color, label, value, formatValue }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: "2px",
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <Typography variant="body2" color="text.secondary">
      {label}
      {value != null ? `: ${formatValue(value)}` : ""}
    </Typography>
  </Box>
);

const GlobalReportGoalsProgressBarChart = ({
  chartConfig = {},
  percent = 0,
  summaryText,
  exceededBy = 0,
  volume = 0,
  achieved = 0,
}) => {
  const { t } = useTranslationApp();

  const achievedColor =
    chartConfig.achieved?.color || GLOBAL_REPORT_GOAL_ACHIEVED_COLOR;
  const remainingColor =
    chartConfig.remaining?.color || GLOBAL_REPORT_GOAL_REMAINING_COLOR;
  const achievedLabel = chartConfig.achieved?.label || "";
  const remainingLabel = chartConfig.remaining?.label || "";
  const displayAchieved = volume > 0 ? Math.min(achieved, volume) : achieved;
  const displayRemaining = volume > 0 ? Math.max(0, volume - achieved) : 0;
  const fillPercent = Math.min(100, Math.max(0, percent));

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="h5"
          color="text.secondary"
          sx={{ fontWeight: 400, lineHeight: 1.2 }}
        >
          {t(VALUE_WITH_PERCENTAGE, { value: percent })}
        </Typography>
        {summaryText ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {summaryText}
          </Typography>
        ) : null}
      </Box>

      <Box
        role="progressbar"
        aria-valuenow={fillPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={summaryText || undefined}
        sx={{
          width: "100%",
          height: PROGRESS_BAR_HEIGHT,
          borderRadius: `${PROGRESS_BAR_RADIUS}px`,
          bgcolor: remainingColor,
          overflow: "hidden",
        }}
      >
        {fillPercent > 0 ? (
          <Box
            sx={{
              width: `${fillPercent}%`,
              height: "100%",
              bgcolor: achievedColor,
              borderRadius:
                fillPercent >= 100
                  ? `${PROGRESS_BAR_RADIUS}px`
                  : `${PROGRESS_BAR_RADIUS}px 0 0 ${PROGRESS_BAR_RADIUS}px`,
            }}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 2,
          mt: 1.5,
        }}
      >
        <LegendItem
          color={achievedColor}
          label={achievedLabel}
          value={displayAchieved}
          formatValue={(value) => t(VALUE_NUMBER, { value })}
        />
        {volume > 0 ? (
          <LegendItem
            color={remainingColor}
            label={remainingLabel}
            value={displayRemaining}
            formatValue={(value) => t(VALUE_NUMBER, { value })}
          />
        ) : null}
      </Box>

      {exceededBy > 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.5, textAlign: "center" }}
        >
          {t(GLOBAL_REPORT_GOALS_EXCEEDED, { count: exceededBy })}
        </Typography>
      ) : null}
    </Box>
  );
};

export default GlobalReportGoalsProgressBarChart;
